import { toast } from "react-toastify";
import { observable } from "mobx";
import { apiOperacao } from "../../api";
import { toast_error } from "../../utils";

export class _SugestoesMelhoriasStore {

  obs = observable({
    loading: false as boolean,
    title_loading: "" as string,
    melhorias_usuario: [] as any,
    melhorias_length: 0 as number,
    status_sugestao: [] as any
  })

  /**
  * Consulta as sugestões de melhoria cadastradas pelo usuário.
  *
  * Ativa o estado de carregamento da tela e envia uma requisição POST para o
  * endpoint responsável por listar as sugestões do usuário de forma paginada.
  * Antes de processar a resposta, limpa a lista atual e redefine a quantidade
  * total de registros.
  *
  * Quando a consulta é realizada com sucesso, atualiza a lista de sugestões
  * e a quantidade total de registros retornados pela API.
  *
  * Em caso de erro na requisição, exibe uma mensagem informando que não foi
  * possível consultar as sugestões. Ao final do processamento, o estado de
  * carregamento é desativado.
  *
  * @async
  * @param {number} page - Página atual da consulta.
  * @param {number} limit - Quantidade de registros por página.
  * @returns {Promise<void>} Não retorna valor diretamente.
  */

  async handleConsultaMelhoriasUsuario(page: number, limit: number) {

    try {

      this.obs.loading = true;
      this.obs.title_loading = "Buscando sugestões!";

      let data = {
        page: page,
        limit: limit
      }

      let resp = await apiOperacao("api").post("/index", data);

      this.obs.melhorias_usuario = [];
      this.obs.melhorias_length = 0;

      if (resp.data.ok) {
        this.obs.melhorias_usuario = resp.data.data;
        this.obs.melhorias_length = resp.data.count;
      }

    } catch (ex) {

      toast_error(ex, `Erro ao consultar sugestões, tente novamente mais tarde.`);

    } finally {

      this.obs.loading = false;

    }
  }

  /**
   * Cadastra uma nova sugestão de melhoria.
   *
   * Recebe um `FormData` já montado pelo componente porque a requisição
   * carrega imagens junto dos campos de texto, e por isso trafega como
   * `multipart/form-data`.
   *
   * O `Content-Type` é anulado de propósito: quando o header não é definido,
   * o browser o monta sozinho incluindo o `boundary` que separa as partes do
   * corpo. Informar `multipart/form-data` na mão, sem boundary, faz o
   * servidor receber um corpo que não consegue separar.
   *
   * @param form Dados da sugestão, incluindo as imagens anexadas.
   * @returns `true` quando a sugestão foi cadastrada.
   */
  async handleCreateMelhoria(form: FormData) {

    try {

      this.obs.loading = true;
      this.obs.title_loading = "Enviando sugestão!";

      let resp = await apiOperacao("api").post("/create", form);

      if (resp.data.ok) {

        toast.success("Sugestão enviada com sucesso!", {});
        return true

      } else {

        toast.error(resp.data.error, {});

      }

    } catch (ex) {

      toast_error(ex, `Erro ao enviar sugestão, tente novamente mais tarde.`);

    } finally {

      this.obs.loading = false;

    }

    return false

  };

  /**
  * Consulta os status disponíveis para sugestões.
  *
  * Envia uma requisição GET para o endpoint responsável por listar os status.
  * Após receber a resposta, atualiza `this.obs.status_sugestao` com os registros
  * retornados pela API.
  *
  * Em caso de erro na requisição, exibe uma mensagem informando que não foi
  * possível consultar os status.
  *
  * @async
  * @returns {Promise<boolean>} Retorna `true` quando a consulta é realizada com
  * sucesso e `false` quando ocorre algum erro ou a resposta não indica sucesso.
  */

  async handleConsultaStatus() {

    try {

      let resp = await apiOperacao("api").get("/status");

      if (resp.data.ok) {
        this.obs.status_sugestao = resp.data.data;
        return true
      }
    } catch (ex) {
      toast_error(ex, `Erro ao consultar status, tente novamente mais tarde.`);
    }

    return false
  }

  /**
  * Consulta os detalhes de uma sugestão pelo identificador informado.
  *
  * Ativa o estado de carregamento da tela e envia uma requisição GET para o
  * endpoint responsável por buscar os detalhes da sugestão. Quando a consulta
  * é realizada com sucesso, retorna os dados recebidos da API.
  *
  * Em caso de erro na requisição, exibe uma mensagem informando que não foi
  * possível consultar os detalhes da sugestão. Ao final do processamento, o
  * estado de carregamento é desativado.
  *
  * @async
  * @param {string} id - Identificador da sugestão que será consultada.
  * @returns {Promise<any | null>} Retorna os dados da sugestão quando a consulta
  * é realizada com sucesso ou `null` em caso de erro ou resposta inválida.
  */

  async handleConsultaDetalhe(id: string) {

    try {

      this.obs.loading = true;
      this.obs.title_loading = "Buscando sugestão!";

      let resp = await apiOperacao("api").get(`/detalhe/${id}`);

      if (resp.data.ok) {
        return resp.data.data;
      }

    } catch (ex) {

      toast_error(ex, `Erro ao consultar detalhes da sugestão.`);

    } finally {

      this.obs.loading = false;

    }

    return null;

  }

  /**
  * Realiza o download de um arquivo associado a uma sugestão.
  *
  * Envia uma requisição GET para o endpoint responsável pelo download do 
  * arquivo informado. A resposta é recebida no formato `blob`, permitindo 
  * o download de arquivos binários como imagens, PDFs e documentos.
  *
  * Quando o download é realizado com sucesso, cria temporariamente uma URL
  * local para o conteúdo recebido, gera um elemento de link e dispara o
  * download utilizando o nome original do arquivo. Após a operação, a URL
  * temporária e o elemento criado são removidos.
  *
  * Caso a API retorne uma resposta JSON em vez do arquivo esperado, exibe
  * uma mensagem informando que não foi possível realizar o download.
  *
  * Em caso de erro na requisição, exibe uma mensagem de erro. Ao final do
  * processamento, o estado de carregamento é desativado.
  *
  * @async
  * @param {string} sugestaoId - Identificador da sugestão à qual o arquivo pertence.
  * @param {number} indice - Índice do arquivo dentro da lista de arquivos da sugestão.
  * @param {string} nomeArquivo - Nome que será utilizado ao salvar o arquivo no dispositivo.
  * @returns {Promise<boolean>} Retorna `true` quando o download é iniciado com sucesso
  * e `false` quando ocorre algum erro ou a resposta da API não contém o arquivo esperado.
  */

  async handleDownloadArquivo(sugestaoId: string, indice: number, nomeArquivo: string) {

    try {

      this.obs.loading = true;
      this.obs.title_loading = "Baixando arquivo!";

      const resp = await apiOperacao("api").get(`/sugestoes/${sugestaoId}/arquivos/${indice}/download`, {responseType: "blob"});

      const contentType = resp.headers["content-type"] || "";

      if (contentType.indexOf("application/json") !== -1) {

        toast.error("Não foi possível baixar o arquivo.");

        return false;

      }

      const url = window.URL.createObjectURL(resp.data);

      const link = document.createElement("a");

      link.href = url;
      link.download = nomeArquivo;

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);

      return true;

    } catch (ex) {

      toast_error(ex, "Erro ao baixar arquivo, tente novamente mais tarde.");

      return false;

    } finally {

      this.obs.loading = false;

    }
  }

}

export const SugestoesMelhoriasStore = new _SugestoesMelhoriasStore();